import { useCallback, useState } from 'react';
import { commissioningProvider, dealerProvider } from '../../services/api';
import { IDynamicFilterData } from '../../interfaces';
import { useStorage } from '../useStorage';
import { toast } from 'react-toastify';

export interface IVendorCode {
  vendedor: string;
  nome: string;
}

export interface ICommission {
  razaoSocial: string;
  numeroNota: string;
  parcela: string;
  dataParcela: string;
  dataPedido: string;
  valorPedido: number;
  valorPago: number;
  status: string;
}

const toastConfig = {
  autoClose: 3000,
  hideProgressBar: true,
  type: 'success',
};

type CommissionFilters = IDynamicFilterData & {
  extraType: string;
  currentPage?: number;
  paginationSize?: number;
  vendorCodeOverride?: string;
};

export const useCommissioning = (): [
  {
    isLoading: boolean;
    error: unknown;
    commissions: ICommission[];
    totalItems: number;
    vendorCodes: IVendorCode[];
  },
  (filters?: CommissionFilters) => void,
  (filters?: CommissionFilters) => Promise<void>,
  () => Promise<void>,
] => {
  const { getItemOnStorage } = useStorage();
  const currentUser = getItemOnStorage('currentUser');
  const user = JSON.parse(currentUser || '');
  const vendorCode = user.vendedorAdicional;

  const [state, setState] = useState<{
    isLoading: boolean;
    error: unknown;
    commissions: ICommission[];
    totalItems: number;
    vendorCodes: IVendorCode[];
  }>({
    isLoading: false,
    error: null,
    commissions: [],
    totalItems: 0,
    vendorCodes: [],
  });

  const fetchCommissions = useCallback(
    async (
      filters: CommissionFilters = {
        type: 'nfe',
        term: '',
        dateStart: '',
        dateEnd: '',
        extraType: '0',
      },
    ) => {
      try {
        setState(prevState => ({ ...prevState, isLoading: true }));

        const effectiveVendorCode =
          filters.vendorCodeOverride !== undefined
            ? filters.vendorCodeOverride
            : vendorCode;
        const vendedorQuery = effectiveVendorCode
          ? `&vendedor=${effectiveVendorCode}`
          : '';

        const response = await commissioningProvider.get(
          `/api/Comissoes/ConsultaPagamentos?type=${filters.type}&term=${filters.term}&dateStart=${filters.dateStart}&dateEnd=${filters.dateEnd}&extraType=${filters.extraType}${vendedorQuery}&page=${filters.currentPage}&pageSize=${filters.paginationSize}`,
        );

        setState(prevState => ({
          ...prevState,
          commissions: response.data.data.pagamentos,
          totalItems: response.data.data.pagination.totalItems,
          error: null,
          isLoading: false,
        }));
      } catch (err: any) {
        const errors: string[] = err?.response?.data?.errors;
        const message =
          Array.isArray(errors) && errors.length > 0
            ? errors.join('\n')
            : 'Erro ao listar comissões.';
        toast.error(message);
        setState(prevState => ({
          ...prevState,
          error: errors ?? err,
          commissions: [],
          totalItems: 0,
        }));
      } finally {
        setState(prevState => ({ ...prevState, isLoading: false }));
      }
    },
    [vendorCode],
  );

  const exportCommissions = useCallback(
    async (
      filters: CommissionFilters = {
        type: 'nfe',
        term: '',
        dateStart: '',
        dateEnd: '',
        extraType: '0',
      },
    ) => {
      try {
        setState(prevState => ({ ...prevState, isLoading: true }));

        const effectiveVendorCode =
          filters.vendorCodeOverride !== undefined
            ? filters.vendorCodeOverride
            : vendorCode;
        const vendedorQuery = effectiveVendorCode
          ? `&vendedor=${effectiveVendorCode}`
          : '';
        const response = await commissioningProvider.get(
          `/api/Comissoes/ExportarPagamentos?type=${filters.type}&term=${filters.term}&dateStart=${filters.dateStart}&dateEnd=${filters.dateEnd}&extraType=${filters.extraType}${vendedorQuery}`,
          {
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'comissoes_export.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err: any) {
        setState(prevState => ({
          ...prevState,
          error: err,
        }));
      } finally {
        setState(prevState => ({ ...prevState, isLoading: false }));
      }
    },
    [vendorCode],
  );

  const fetchVendorCodes = useCallback(async () => {
    try {
      const response = await dealerProvider.get(
        '/api/obter-codigo-revendedores',
      );
      const vendorCodes = response.data.data.revendedores;
      setState(prevState => ({
        ...prevState,
        vendorCodes,
      }));
    } catch (err: any) {
      toast.error(
        err.response?.data?.errors?.[0] ||
          'Erro ao buscar códigos de vendedor.',
      );
    }
  }, []);

  return [state, fetchCommissions, exportCommissions, fetchVendorCodes];
};
